import React, { useEffect, useState } from 'react';
import './LearningModules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import QuizIcon from '@mui/icons-material/Quiz';
import { motion, AnimatePresence } from 'framer-motion';
import { getCertificationModules, getCertificationDetail } from '../../../api/certification';

export default function LearningModules() {
  const { certificationId } = useParams();
  const navigate = useNavigate();
  const enrollmentMap = useSelector((s) => s.certification.enrollmentMap);
  const localEnrollment = enrollmentMap[certificationId];
  const applicationId = localEnrollment?.applicationId ?? null;

  const [modules, setModules] = useState([]);
  const [certName, setCertName] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [visited, setVisited] = useState({}); // { [moduleId]: true }
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [modsRes, detailRes] = await Promise.all([
          getCertificationModules(certificationId),
          getCertificationDetail(certificationId),
        ]);
        const mods = modsRes?.data ?? modsRes ?? [];
        const detail = detailRes?.data ?? detailRes;
        setModules(Array.isArray(mods) ? mods : []);
        setCertName(detail?.name ?? detail?.certificationName ?? 'Certification');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else if (status === 403) {
          setError('You must be enrolled to access this.');
        } else {
          setError(e?.response?.data?.message || 'Failed to load modules.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [certificationId, navigate]);

  const handleToggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
    // Mark as visited when opened
    setVisited((prev) => ({ ...prev, [id]: true }));
  };

  const visitedCount = Object.keys(visited).length;
  const totalCount = modules.length;
  const progressPct = totalCount > 0 ? Math.round((visitedCount / totalCount) * 100) : 0;

  const handleTakeExam = () => {
    if (applicationId) {
      navigate(`/certifications/${certificationId}/exam/${applicationId}`);
    } else {
      navigate(`/certifications/${certificationId}`);
    }
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div className="lm-page">
        <div className="lm-container">
          <div className="lm-back" onClick={() => navigate(`/certifications/${certificationId}`)}>
            <ArrowBackIcon style={{ fontSize: 16 }} /> Back
          </div>
          <div className="lm-header-skeleton">
            <div className="lm-skeleton lm-skel-icon" />
            <div>
              <div className="lm-skeleton lm-skel-title" />
              <div className="lm-skeleton lm-skel-sub" />
            </div>
          </div>
          <div className="lm-skeleton lm-skel-progress" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="lm-skeleton lm-skel-card" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lm-page">
        <div className="lm-container">
          <button className="lm-back" onClick={() => navigate(`/certifications/${certificationId}`)}>
            <ArrowBackIcon style={{ fontSize: 16 }} /> Back
          </button>
          <div className="lm-error-card">
            <p>{error}</p>
            <button className="lm-btn-exam" onClick={() => navigate(`/certifications/${certificationId}`)}>
              Go to Certification
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lm-page">
      <div className="lm-container">
        {/* Back */}
        <button className="lm-back" onClick={() => navigate(`/certifications/${certificationId}`)}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Back to {certName}
        </button>

        {/* Header */}
        <div className="lm-header">
          <div className="lm-header-icon-wrap">
            <SchoolIcon className="lm-header-icon" />
          </div>
          <div>
            <h1 className="lm-title">Learning Modules</h1>
            <p className="lm-subtitle">{certName}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="lm-progress-card">
          <div className="lm-progress-top">
            <span className="lm-progress-label">Your Progress</span>
            <span className="lm-progress-count">{visitedCount} / {totalCount} modules reviewed</span>
          </div>
          <div className="lm-progress-bar-track">
            <motion.div
              className="lm-progress-bar-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <span className="lm-progress-pct">{progressPct}%</span>
        </div>

        {/* Module accordion list */}
        {modules.length === 0 ? (
          <div className="lm-empty">No modules available for this certification yet.</div>
        ) : (
          <div className="lm-modules-list">
            {modules.map((mod, idx) => {
              const modId = mod.moduleId ?? mod.id ?? idx;
              const isOpen = openId === modId;
              const isVisited = !!visited[modId];

              return (
                <motion.div
                  key={modId}
                  className={`lm-module-card${isOpen ? ' lm-module-open' : ''}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, delay: idx * 0.04 }}
                >
                  {/* Accordion header */}
                  <button
                    className="lm-module-header"
                    onClick={() => handleToggle(modId)}
                  >
                    <div className="lm-module-header-left">
                      <div className="lm-module-num">{idx + 1}</div>
                      <div className="lm-module-title-block">
                        <span className="lm-module-title">{mod.title ?? mod.moduleName}</span>
                        {mod.description && !isOpen && (
                          <span className="lm-module-preview">{mod.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="lm-module-header-right">
                      {isVisited
                        ? <CheckCircleIcon className="lm-visited-icon" />
                        : <RadioButtonUncheckedIcon className="lm-unvisited-icon" />
                      }
                      <ExpandMoreIcon
                        className={`lm-chevron${isOpen ? ' lm-chevron-open' : ''}`}
                      />
                    </div>
                  </button>

                  {/* Accordion body */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="lm-module-body"
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="lm-module-content">
                          {mod.content ?? mod.description ?? 'No content available for this module.'}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Take Exam CTA */}
        <div className="lm-exam-cta">
          <div className="lm-exam-cta-inner">
            <div className="lm-exam-cta-text">
              <QuizIcon className="lm-exam-icon" />
              <div>
                <div className="lm-exam-cta-title">Ready to take the exam?</div>
                <div className="lm-exam-cta-sub">
                  {visitedCount < totalCount
                    ? `Review all ${totalCount} modules before you begin.`
                    : 'You\'ve reviewed all modules. Good luck!'}
                </div>
              </div>
            </div>
            <button className="lm-btn-exam" onClick={handleTakeExam}>
              Take Exam
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
